import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, IconButton, Button, Select } from '@chakra-ui/react';
import { MdDelete, MdSearch, MdEdit } from 'react-icons/md';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import StrongInput from '../components/common/StrongInput';
import EditWarmupModal from '../components/warmups/EditWarmupModal';
import { WarmupTemplate } from '../lib/types/programEdit';

const WARMUP_COLUMNS = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'circuitsCount',
    label: 'Circuits Count',
    render: (_value: any, row: WarmupTemplate) => row.circuits?.length || 0,
  },
  {
    key: 'exercisesCount',
    label: 'Exercises Count',
    render: (_value: any, row: WarmupTemplate) => {
      const circuitsExs = row.circuits?.reduce((acc, c) => acc + (c.circuitExercises?.length || 0), 0) || 0;
      const standaloneExs = row.exercises?.length || 0;
      return circuitsExs + standaloneExs;
    },
  },
];

const {
  getWarmups: getWarmupsUrl,
  createWarmup: createWarmupUrl,
  editWarmup: editWarmupUrl,
  deleteWarmup: deleteWarmupUrl,
} = urlConstants.warmups;

type GetWarmupsResponse = {
  warmups: WarmupTemplate[];
  pagination: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
};

const Warmups = () => {
  const [warmups, setWarmups] = useState<WarmupTemplate[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedWarmup, setSelectedWarmup] = useState<WarmupTemplate>();
  const [sortCriteria, setSortCriteria] = useState<string>('NewestAdded');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const showToast = useToastNotification();

  const { loading: isGetWarmupsLoading, sendRequest: getWarmups } =
    useApi<GetWarmupsResponse>();
  const { loading: isCreateWarmupLoading, sendRequest: createWarmup } =
    useApi<any>();
  const { loading: isEditWarmupLoading, sendRequest: editWarmup } =
    useApi<any>();
  const { loading: isDeleteWarmupLoading, sendRequest: deleteWarmup } =
    useApi<any>();

  const fetchWarmups = useCallback(() => {
    getWarmups({
      callback: (data: GetWarmupsResponse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setWarmups(data.warmups);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getWarmupsUrl,
      options: {
        page: currentPage,
        limit: pageSize,
        search: searchQuery,
        sortBy: sortCriteria,
      },
    });
  }, [getWarmups, currentPage, pageSize, searchQuery, sortCriteria, showToast]);

  useEffect(() => {
    fetchWarmups();
  }, [fetchWarmups]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSaveWarmup = (
    title: string,
    instructions: string,
    circuits: any[],
    exercises: any[],
  ) => {
    if (selectedWarmup) {
      editWarmup({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Warmup updated successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchWarmups();
        },
        command: ApiCommand.PUT,
        url: editWarmupUrl,
        options: {
          warmupId: selectedWarmup._id || selectedWarmup.id,
          title,
          instructions,
          circuits,
          exercises,
        },
      });
    } else {
      createWarmup({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Warmup created successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchWarmups();
        },
        command: ApiCommand.POST,
        url: createWarmupUrl,
        options: {
          title,
          instructions,
          circuits,
          exercises,
        },
      });
    }
  };

  const onDeleteWarmup = (warmupId: string) => {
    if (!warmupId) return;
    deleteWarmup({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'Warmup deleted successfully',
          status: 'success',
        });
        fetchWarmups();
      },
      command: ApiCommand.DELETE,
      url: deleteWarmupUrl,
      options: {
        warmupId,
      },
    });
  };

  const onEditWarmup = (warmup: WarmupTemplate) => {
    setSelectedWarmup(warmup);
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box backgroundColor='white' borderRadius='lg' padding={2}>
          <Flex justifyContent='space-between' marginBottom={4} gap={4}>
            <Box flex='1' maxW='400px'>
              <StrongInput
                placeholder='Search'
                rightIcon={<MdSearch color='gray.500' />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
            <Select
              value={sortCriteria}
              onChange={(e) => setSortCriteria(e.target.value)}
              width='20%'
            >
              <option value='NameAtoZ'>A-Z</option>
              <option value='NameZtoA'>Z-A</option>
              <option value='NewestAdded'>Newest</option>
              <option value='OldestAdded'>Oldest</option>
            </Select>
            <Button
              colorScheme='primary'
              px={6}
              py={4}
              borderRadius='md'
              fontWeight='bold'
              _hover={{ bg: 'primary.300' }}
              onClick={() => {
                setSelectedWarmup(undefined);
                setIsOpenModal(true);
              }}
            >
              Create Warmup
            </Button>
          </Flex>

          <StrongTable
            columns={WARMUP_COLUMNS}
            data={warmups}
            loading={isGetWarmupsLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit Warmup'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEditWarmup(row)}
                />
                <IconButton
                  aria-label='Delete Warmup'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDeleteWarmup(row._id || row.id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditWarmupModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveWarmup}
        selectedWarmup={selectedWarmup}
      />
    </Page>
  );
};

export default Warmups;
